import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ShieldCheck, Trash, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const REGIONS = ["eu-central", "eu-west", "us-east", "ap-south"];

function Section({ Icon, title, children }) {
  return (
    <div className="bg-card border border-border rounded-sm">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Icon size={18} className="text-primary" weight="bold" />
        <h3 className="font-heading font-bold tracking-tight">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function Gdpr() {
  const { t } = useTranslation();
  const { currentOrg, loadOrgs } = useOrg();
  const [region, setRegion] = useState("");
  const [consents, setConsents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [consent, setConsent] = useState({ subject: "", purpose: "", granted: true });
  const [erasure, setErasure] = useState({ subject: "", reason: "" });

  const loadAll = () => {
    api.get("/gdpr/residency").then((r) => setRegion(r.data.region)).catch(() => {});
    api.get("/gdpr/consents").then((r) => setConsents(r.data)).catch(() => {});
    api.get("/gdpr/audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) loadAll(); }, [currentOrg]);

  const updateRegion = async () => {
    try { await api.put("/gdpr/residency", { region }); await loadOrgs(); toast.success("Updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addConsent = async () => {
    try {
      await api.post("/gdpr/consents", consent);
      setConsent({ subject: "", purpose: "", granted: true });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doErasure = async () => {
    if (!window.confirm(`Erase all data for "${erasure.subject}"?`)) return;
    try {
      const { data } = await api.post("/gdpr/erasure", erasure);
      toast.success(`Erased. Receipt ${data.receipt_id.slice(0, 8)}`);
      setErasure({ subject: "", reason: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title={t("gdpr.title")} subtitle={t("gdpr.subtitle")} />

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Section Icon={Globe} title={t("gdpr.residency")}>
          <div className="flex items-end gap-3">
            <div className="space-y-2 flex-1">
              <Label>{t("gdpr.region")}</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="rounded-sm font-mono" data-testid="region-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => <SelectItem key={r} value={r} className="font-mono">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={updateRegion} className="rounded-sm" data-testid="update-region-button">{t("gdpr.updateRegion")}</Button>
          </div>
        </Section>

        <Section Icon={Trash} title={t("gdpr.erasure")}>
          <p className="text-sm text-muted-foreground mb-3">{t("gdpr.erasureDesc")}</p>
          <div className="space-y-3">
            <Input placeholder={t("gdpr.subject")} value={erasure.subject} onChange={(e) => setErasure({ ...erasure, subject: e.target.value })} className="rounded-sm" data-testid="erasure-subject-input" />
            <Input placeholder={t("gdpr.reason")} value={erasure.reason} onChange={(e) => setErasure({ ...erasure, reason: e.target.value })} className="rounded-sm" data-testid="erasure-reason-input" />
            <Button variant="destructive" onClick={doErasure} disabled={!erasure.subject.trim()} className="rounded-sm w-full" data-testid="erasure-submit-button">{t("gdpr.erase")}</Button>
          </div>
        </Section>
      </div>

      <Section Icon={ShieldCheck} title={t("gdpr.consent")}>
        <div className="grid md:grid-cols-4 gap-3 items-end mb-4">
          <div className="space-y-2"><Label>{t("gdpr.subject")}</Label><Input value={consent.subject} onChange={(e) => setConsent({ ...consent, subject: e.target.value })} className="rounded-sm" data-testid="consent-subject-input" /></div>
          <div className="space-y-2"><Label>{t("gdpr.purpose")}</Label><Input value={consent.purpose} onChange={(e) => setConsent({ ...consent, purpose: e.target.value })} className="rounded-sm" data-testid="consent-purpose-input" /></div>
          <div className="flex items-center gap-2"><Switch checked={consent.granted} onCheckedChange={(v) => setConsent({ ...consent, granted: v })} data-testid="consent-granted-switch" /><Label>{t("gdpr.granted")}</Label></div>
          <Button onClick={addConsent} disabled={!consent.subject.trim() || !consent.purpose.trim()} className="rounded-sm" data-testid="add-consent-button">{t("gdpr.addConsent")}</Button>
        </div>
        <div className="border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>{t("gdpr.subject")}</TableHead><TableHead>{t("gdpr.purpose")}</TableHead><TableHead>{t("gdpr.granted")}</TableHead><TableHead>{t("gdpr.time")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {consents.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs">{c.subject}</TableCell>
                  <TableCell>{c.purpose}</TableCell>
                  <TableCell><Badge variant={c.granted ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">{String(c.granted)}</Badge></TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.created_at.slice(0, 19).replace("T", " ")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      <div className="mt-6">
        <Section Icon={ClockCounterClockwise} title={t("gdpr.audit")}>
          <div className="border border-border rounded-sm overflow-x-auto max-h-[420px] overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>{t("gdpr.action")}</TableHead><TableHead>{t("gdpr.actor")}</TableHead><TableHead>{t("gdpr.target")}</TableHead><TableHead>{t("gdpr.time")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {audit.map((a) => (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell><Badge variant="outline" className="rounded-sm font-mono text-[10px]">{a.action}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{a.actor_email || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{a.target || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{(a.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      </div>
    </div>
  );
}
